import React, { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { Route } from "react-router";
import { Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { Button, ButtonVariants, NavigateButton } from "affinityUI/elements/button";
import { Card, Container } from "affinityUI/elements/card";
import { StatusView } from "affinityUI/features/statusView";
import { ButtonGroup } from "affinityUI/form/components";
import { Layout } from "affinityUI/structures/layout";
import { IssueDeviceView } from "components/views/patientDetails/device/issueDevice";
import { EnrollPatient, useGetPatientResultsQuery } from "gql";

import deviceIssued from "./assets/issued.svg";
import { PatientInfo } from "./components/patientInfo";

const StatusContainer = styled(Container)`
  border-top: 1px solid ${(props) => props.theme.colours.bordersLight};
`;

export const IssueDevice: React.FC = () => {
  const { id } = useParams<{ id: string; resultId: string }>();
  const { showBoundary: onError } = useErrorBoundary();
  const navigate = useNavigate();

  const { loading, data } = useGetPatientResultsQuery({ onError, variables: { id: id as string }, errorPolicy: "all" });
  const patient = data?.patient;
  const patientId = patient?.id;

  const [enrollPatient, { loading: loadingEnrollment }] = useMutation(EnrollPatient, {
    onError,
  });
  const handleIssueClick = useCallback(async () => {
    if (!isNil(patientId)) {
      await enrollPatient({
        variables: { dryRun: false, patientId: patientId, allocationDate: new Date().toISOString() },
      });
      navigate("..");
    }
  }, [enrollPatient, navigate, patientId]);

  const actions = (
    <ButtonGroup>
      <NavigateButton to="../.." variant={ButtonVariants.SECONDARY}>
        Cancel
      </NavigateButton>
      <Button disabled={loadingEnrollment} onClick={handleIssueClick} variant={ButtonVariants.PRIMARY}>
        {loadingEnrollment ? "Sending request" : "Issue Device"}
      </Button>
    </ButtonGroup>
  );
  return (
    <Layout
      title={!isNil(patient) ? `Issue device to ${patient?.firstName} ${patient?.lastName}` : "Issue device"}
      showBackBtn
      backLink=".."
      loading={loading}
    >
      <Card>
        {!isNil(patient) ? <PatientInfo patient={patient} /> : <></>}
        <Routes>
          <Route
            index
            element={
              <IssueDeviceView actions={<NavigateButton to="confirm">Continue to Confirmation</NavigateButton>} />
            }
          />
          <Route
            path="/confirm"
            element={
              <StatusContainer>
                <StatusView imgSrc={deviceIssued} heading="Please confirm" actions={actions}>
                  Are you sure you want to send a device to the above address?
                </StatusView>
              </StatusContainer>
            }
          />
        </Routes>
      </Card>
    </Layout>
  );
};
